import React, { useEffect, useState } from "react";

import { toast } from "../../../utils/notify";

import { Eye } from "lucide-react";

import ApiLoader from "../../ui/ApiLoader";
import { DataTable } from "../../common/DataTable";

import {
  fetchRisks,
  fetchRiskById,
} from "../../../services/axios/AuthApis/DashboardApis";

const Risk = ({ activeTab }) => {
  const [risks, setRisks] = useState([]);

  const [filteredRisks, setFilteredRisks] = useState([]);

  const [riskLoading, setRiskLoading] = useState(false);

  const [riskSearch, setRiskSearch] = useState("");

  const [selectedRisk, setSelectedRisk] = useState(null);

  const [showRiskModal, setShowRiskModal] = useState(false);

  const loadRisks = async () => {
    try {
      setRiskLoading(true);

      const response = await fetchRisks();

      const data = response.data || response || [];

      setRisks(data);

      setFilteredRisks(data);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load risks");
    } finally {
      setRiskLoading(false);
    }
  };

  const handleViewRisk = async (id) => {
    try {
      const response = await fetchRiskById(id);

      setSelectedRisk(response.data || response);

      setShowRiskModal(true);
    } catch (error) {
      console.log(error);

      toast.error("Unable to load risk details");
    }
  };

  const handleRiskSearch = (e) => {
    const value = e.target.value.toLowerCase();

    setRiskSearch(value);

    const filtered = risks.filter(
      (item) =>
        item.title?.toLowerCase().includes(value) ||
        item.asset_name?.toLowerCase().includes(value) ||
        item.severity?.toLowerCase().includes(value),
    );

    setFilteredRisks(filtered);
  };

  useEffect(() => {
    if (activeTab === "risks") {
      loadRisks();
    }
  }, [activeTab]);

  const riskColumns = [
    {
      id: "title",
      header: "Risk Name",
      meta: { filter: { variant: "text", label: "Risk Name", placeholder: "Search risk" } },
      cell: ({ row }) => <span>{row.original.title || row.original.risk_name || "-"}</span>,
    },
    {
      id: "asset",
      header: "Asset",
      cell: ({ row }) => <span>{row.original.asset_name || row.original.asset || "-"}</span>,
    },
    {
      accessorKey: "severity",
      header: "Severity",
      cell: ({ getValue }) => <span className="font-semibold">{getValue() || "Low"}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => <span className="px-3 py-1 rounded-full text-sm">{getValue() || "-"}</span>,
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => (
        <button onClick={() => handleViewRisk(row.original._id || row.original.id)} className="border rounded-lg p-2">
          <Eye size={18} />
        </button>
      ),
    },
  ];

  if (riskLoading) {
    return (
      <ApiLoader
        variant="spinner"
        label="Loading Risk..."
        className="py-20"
      />
    );
  }

  return (
    <>
      {activeTab === "risks" && (
        <>
          <h2 className="text-2xl font-bold mb-6">Risks</h2>

          {/* Summary Cards */}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">Total Risks</p>

              <h2 className="text-3xl font-bold mt-3">{risks.length}</h2>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">Critical Risks</p>

              <h2 className="text-3xl font-bold mt-3 text-red-600">
                {risks.filter((item) => item.severity === "critical").length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">High Risks</p>

              <h2 className="text-3xl font-bold mt-3 text-orange-600">
                {risks.filter((item) => item.severity === "high").length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">Resolved Risks</p>

              <h2 className="text-3xl font-bold mt-3 text-green-600">
                {risks.filter((item) => item.status === "resolved").length}
              </h2>
            </div>
          </div>

          {/* Risk Table */}

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold">Risk Inventory</h2>

              <input
                type="text"
                placeholder="Search Risks..."
                value={riskSearch}
                onChange={handleRiskSearch}
                className="border rounded-lg px-4 py-2 w-64"
              />
            </div>

            <DataTable
              columns={riskColumns}
              data={filteredRisks}
              getRowId={(risk) => String(risk._id || risk.id)}
              loading={riskLoading}
              emptyMessage="No Risks Found"
              enableColumnVisibility={false}
              enableColumnResizing
              maxBodyHeight="65vh"
            />
          </div>

          {/* ================= Risk Details Modal ================= */}

          {showRiskModal && selectedRisk && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl w-[600px] p-6">
                <div className="flex justify-between items-center mb-5">
                  <h2 className="text-xl font-bold">Risk Details</h2>

                  <button
                    onClick={() => {
                      setShowRiskModal(false);
                      setSelectedRisk(null);
                    }}
                    className="text-xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-bold uppercase text-black mb-2">
                      Risk Description
                    </h3>

                    <div className="border rounded-xl p-4 text-white bg-blue-600">
                      {selectedRisk.description || "-"}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-x-10 gap-y-5">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Risk Name
                      </p>
                      <p className="font-medium">
                        {selectedRisk.risk_name || selectedRisk.title || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Asset</p>
                      <p className="font-medium">
                        {selectedRisk.asset_name || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Severity
                      </p>

                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-600">
                        {selectedRisk.severity || "-"}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Status
                      </p>

                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-600">
                        {selectedRisk.status || "-"}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Owner</p>
                      <p>{selectedRisk.owner || "-"}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Created Date
                      </p>
                      <p>{selectedRisk.created_at || "-"}</p>
                    </div>
                  </div>

                  {/* Remediation */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase text-gray-500 mb-2">
                      Remediation
                    </h3>

                    <div className="border rounded-xl p-4 bg-gray-50">
                      {selectedRisk.remediation || "-"}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowRiskModal(false);
                      setSelectedRisk(null);
                    }}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Risk;
